import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Bell, Send, Trash2, Users } from "lucide-react";

interface Profile { user_id: string; email: string; full_name: string | null; numeric_id: string | null; }
interface Notif { id: string; title: string; body: string; category: string; user_id: string | null; created_at: string; }

const AdminNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<"all" | "one">("all");
  const [targetId, setTargetId] = useState("");
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [items, setItems] = useState<Notif[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    supabase.from("profiles").select("user_id,email,full_name,numeric_id").limit(500)
      .then(({ data }) => setProfiles((data || []) as any));
    load();
    const ch = supabase.channel("admin-notifs")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const load = async () => {
    const { data } = await supabase.from("notifications").select("*")
      .eq("category", "admin").order("created_at", { ascending: false }).limit(50);
    setItems((data || []) as any);
  };

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast({ title: "أدخل عنواناً ونصاً", variant: "destructive" }); return; }
    setSubmitting(true);
    try {
      let userIds: (string | null)[] = [];
      if (target === "all") {
        userIds = [null]; // broadcast
      } else {
        const p = profiles.find(x => x.user_id === targetId || x.numeric_id === targetId || x.email === targetId);
        if (!p) { toast({ title: "المستخدم غير موجود", variant: "destructive" }); setSubmitting(false); return; }
        userIds = [p.user_id];
      }
      const rows = userIds.map(uid => ({
        user_id: uid, title, body, category: "admin", created_by: user?.id,
      }));
      const { error } = await supabase.from("notifications").insert(rows);
      if (error) throw error;
      toast({ title: "تم إرسال الإشعار ✅" });
      setTitle(""); setBody(""); setTargetId("");
    } catch (e: any) {
      toast({ title: "فشل الإرسال", description: e.message, variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const del = async (id: string) => {
    await supabase.from("notifications").delete().eq("id", id);
    toast({ title: "تم الحذف" });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> إرسال إشعار</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>الوجهة</Label>
            <Select value={target} onValueChange={(v: any) => setTarget(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><Users className="w-4 h-4 inline mr-2" /> جميع المستخدمين</SelectItem>
                <SelectItem value="one">مستخدم محدد</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {target === "one" && (
            <div>
              <Label>البريد الإلكتروني أو الـ ID الرقمي</Label>
              <Input value={targetId} onChange={e => setTargetId(e.target.value)} placeholder="user@example.com أو 1234..." />
            </div>
          )}
          <div>
            <Label>العنوان</Label>
            <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="عنوان الإشعار" />
          </div>
          <div>
            <Label>النص</Label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="محتوى الإشعار..." rows={4} />
          </div>
          <Button onClick={send} disabled={submitting} className="w-full gap-2">
            <Send className="w-4 h-4" /> {submitting ? "جاري الإرسال..." : "إرسال"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>الإشعارات المرسلة مؤخراً</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">لا توجد إشعارات</p>
          ) : (
            <div className="space-y-2">
              {items.map(n => (
                <div key={n.id} className="p-3 rounded-lg border border-border flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm">{n.title}</span>
                      <Badge variant="outline" className="text-[10px]">
                        {n.user_id ? "مستخدم واحد" : "جميع المستخدمين"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => del(n.id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNotifications;
